// src/jsx/pages/reports/PanchayathBillingReceiptReportPage.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  CheckCircle,
  Download,
  FileText,
  RefreshCcw,
  Search,
  TrendingUp,
  AlertCircle,
} from "react-feather";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  toast,
} from "react-toastify";

import {
  clearPanchayathSubscriptionReportError,
  exportPanchayathSubscriptionReportThunk,
  fetchPanchayathSubscriptionReportThunk,
  REPORT_TYPES,
} from "../../../store/slices/panchayathSubscriptionReportsSlice";

import {
  fetchPanchayathsThunk,
} from "../../../store/slices/panchayathMasterSlice";

import "./ReportPage.css";

const REPORT_TYPE =
  REPORT_TYPES.BILLING_RECEIPT;

const today = () => {
  const d =
    new Date();

  return `${d.getFullYear()}-${String(
    d.getMonth() + 1
  ).padStart(
    2,
    "0"
  )}-${String(
    d.getDate()
  ).padStart(
    2,
    "0"
  )}`;
};

const firstDayOfMonth = () => {
  const d =
    new Date();

  return `${d.getFullYear()}-${String(
    d.getMonth() + 1
  ).padStart(
    2,
    "0"
  )}-01`;
};

const money = (value) =>
  new Intl.NumberFormat(
    "en-IN",
    {
      style:
        "currency",
      currency:
        "INR",
      minimumFractionDigits:
        2,
      maximumFractionDigits:
        2,
    }
  ).format(
    Number(value || 0)
  );

const labelize = (value) =>
  String(value || "")
    .replaceAll(
      "_",
      " "
    )
    .replace(
      /\b\w/g,
      (c) =>
        c.toUpperCase()
    );

const safe = (
  value,
  fallback = "—"
) =>
  value === null ||
  value === undefined ||
  value === ""
    ? fallback
    : value;

const downloadBlob = (
  blob,
  fileName
) => {
  const url =
    window.URL.createObjectURL(
      blob
    );

  const a =
    document.createElement(
      "a"
    );

  a.href =
    url;

  a.download =
    fileName;

  document.body.appendChild(
    a
  );

  a.click();
  a.remove();

  window.URL.revokeObjectURL(
    url
  );
};

const ReportPagination = ({
  loading,
  page,
  totalPages,
  setPage,
}) => {
  if (
    totalPages <= 1
  ) {
    return null;
  }

  return (
    <Pagination className="mb-0">
      <Pagination.First
        disabled={
          loading ||
          page <= 1
        }
        onClick={() =>
          setPage(1)
        }
      />

      <Pagination.Prev
        disabled={
          loading ||
          page <= 1
        }
        onClick={() =>
          setPage(
            page - 1
          )
        }
      />

      <Pagination.Item
        active
      >
        {page}
      </Pagination.Item>

      <Pagination.Next
        disabled={
          loading ||
          page >=
            totalPages
        }
        onClick={() =>
          setPage(
            page + 1
          )
        }
      />

      <Pagination.Last
        disabled={
          loading ||
          page >=
            totalPages
        }
        onClick={() =>
          setPage(
            totalPages
          )
        }
      />
    </Pagination>
  );
};

const PanchayathBillingReceiptReportPage =
  () => {
    const dispatch =
      useDispatch();

    const report =
      useSelector(
        (state) =>
          state
            .panchayathSubscriptionReports
            ?.billing_receipt || {
            rows: [],
            meta: {},
          }
      );

    const {
      loading = false,
      exportLoading = false,
      error = null,
    } =
      useSelector(
        (state) =>
          state
            .panchayathSubscriptionReports ||
          {}
      );

    const {
      rows:
        panchayaths = [],
    } =
      useSelector(
        (state) =>
          state
            .panchayathMaster ||
          {}
      );

    const rows =
      report.rows || [];

    const meta =
      report.meta || {};

    const summary =
      meta.summary || {};

    const [
      searchInput,
      setSearchInput,
    ] =
      useState("");

    const [
      search,
      setSearch,
    ] =
      useState("");

    const [
      panId,
      setPanId,
    ] =
      useState("");

    const [
      category,
      setCategory,
    ] =
      useState("");

    const [
      status,
      setStatus,
    ] =
      useState("");

    const [
      generatedType,
      setGeneratedType,
    ] =
      useState("");

    const [
      fromDate,
      setFromDate,
    ] =
      useState(
        firstDayOfMonth()
      );

    const [
      toDate,
      setToDate,
    ] =
      useState(
        today()
      );

    const [
      page,
      setPage,
    ] =
      useState(1);

    const [
      pageSize,
      setPageSize,
    ] =
      useState(20);

    const [
      sortField,
      setSortField,
    ] =
      useState(
        "bill_date"
      );

    const [
      sortDirection,
      setSortDirection,
    ] =
      useState(
        "desc"
      );

    useEffect(
      () => {
        dispatch(
          fetchPanchayathsThunk()
        );
      },
      [dispatch]
    );

    const filters =
      useMemo(
        () => ({
          page,
          pageSize,

          search,
          pan_id:
            panId,

          category,
          status,

          generated_type:
            generatedType,

          from_date:
            fromDate,

          to_date:
            toDate,

          sort_field:
            sortField,

          sort_direction:
            sortDirection,
        }),
        [
          page,
          pageSize,
          search,
          panId,
          category,
          status,
          generatedType,
          fromDate,
          toDate,
          sortField,
          sortDirection,
        ]
      );

    const loadReport =
      (
        override = {}
      ) =>
        dispatch(
          fetchPanchayathSubscriptionReportThunk({
            reportType:
              REPORT_TYPE,

            filters: {
              ...filters,
              ...override,
            },
          })
        );

    useEffect(
      () => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [
        page,
        pageSize,
        search,
        panId,
        category,
        status,
        generatedType,
        fromDate,
        toDate,
        sortField,
        sortDirection,
      ]
    );

    useEffect(
      () => {
        if (!error) {
          return;
        }

        toast.error(
          String(error)
        );

        dispatch(
          clearPanchayathSubscriptionReportError()
        );
      },
      [
        error,
        dispatch,
      ]
    );

    const submitSearch =
      (e) => {
        e?.preventDefault();

        setPage(1);

        setSearch(
          searchInput.trim()
        );
      };

    const clearFilters =
      () => {
        setSearchInput("");
        setSearch("");
        setPanId("");
        setCategory("");
        setStatus("");
        setGeneratedType("");
        setFromDate(
          firstDayOfMonth()
        );
        setToDate(
          today()
        );
        setPage(1);
        setPageSize(20);
        setSortField(
          "bill_date"
        );
        setSortDirection(
          "desc"
        );
      };

    const handleExport =
      async (
        format
      ) => {
        try {
          const result =
            await dispatch(
              exportPanchayathSubscriptionReportThunk({
                reportType:
                  REPORT_TYPE,

                format,

                filters: {
                  ...filters,
                  page:
                    1,
                },
              })
            ).unwrap();

          downloadBlob(
            result.blob,

            `panchayath_billing_receipt_report.${
              format ===
              "excel"
                ? "xlsx"
                : "pdf"
            }`
          );
        } catch (e) {
          toast.error(
            String(
              e ||
                "Export failed"
            )
          );
        }
      };

    const totalRecords =
      Number(
        meta.totalRecords ||
          0
      );

    const totalPages =
      Math.max(
        1,
        Number(
          meta.totalPages ||
            1
        )
      );

    const currentPage =
      Math.max(
        1,
        Number(
          meta.page ||
            page
        )
      );

    const toggleSort =
      (
        field
      ) => {
        setPage(1);

        setSortField(
          field
        );

        setSortDirection(
          (
            previous
          ) =>
            sortField ===
              field &&
            previous ===
              "asc"
              ? "desc"
              : "asc"
        );
      };

    const headerButton =
      (
        label,
        field
      ) => (
        <button
          type="button"
          className="report-sort-btn"
          onClick={() =>
            toggleSort(
              field
            )
          }
        >
          {label}
        </button>
      );

    return (
      <div className="report-page">
        <Card className="report-card">
          <Card.Header className="report-card-header">
            <div className="report-header-row">
              <div>
                <div className="report-title">
                  Billing & Receipt Report
                </div>

                <div className="report-subtitle">
                  Combined Panchayath billing, GST, invoice, receipt and outstanding report.
                </div>
              </div>
            </div>
          </Card.Header>

          <Card.Body>
            <Row className="g-3 mb-3">
              <Col
                xl={3}
                md={6}
              >
                <div className="border rounded p-3 h-100">
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <FileText size={16} />
                    Billing Amount
                  </div>

                  <strong className="d-block fs-5 mt-2">
                    {money(
                      summary.total_bill_amount
                    )}
                  </strong>
                </div>
              </Col>

              <Col
                xl={3}
                md={6}
              >
                <div className="border rounded p-3 h-100">
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <TrendingUp size={16} />
                    GST Amount
                  </div>

                  <strong className="d-block fs-5 mt-2">
                    {money(
                      summary.total_gst_amount
                    )}
                  </strong>
                </div>
              </Col>

              <Col
                xl={3}
                md={6}
              >
                <div className="border rounded p-3 h-100">
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <CheckCircle size={16} />
                    Approved Received
                  </div>

                  <strong className="d-block fs-5 mt-2">
                    {money(
                      summary.approved_received
                    )}
                  </strong>
                </div>
              </Col>

              <Col
                xl={3}
                md={6}
              >
                <div className="border rounded p-3 h-100">
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <AlertCircle size={16} />
                    Outstanding
                  </div>

                  <strong className="d-block fs-5 mt-2">
                    {money(
                      summary.outstanding_balance
                    )}
                  </strong>
                </div>
              </Col>
            </Row>

            <div className="report-filter-box">
              <Row className="g-3">
                <Col
                  xl={3}
                  md={6}
                >
                  <Form
                    onSubmit={
                      submitSearch
                    }
                  >
                    <Form.Label className="report-label">
                      Search
                    </Form.Label>

                    <InputGroup className="report-search">
                      <InputGroup.Text>
                        <Search size={16} />
                      </InputGroup.Text>

                      <Form.Control
                        value={
                          searchInput
                        }
                        placeholder="Panchayath / Bill / Invoice / Receipt"
                        onChange={(
                          e
                        ) =>
                          setSearchInput(
                            e.target.value
                          )
                        }
                      />

                      <Button
                        type="submit"
                      >
                        Search
                      </Button>
                    </InputGroup>
                  </Form>
                </Col>

                <Col
                  xl={3}
                  md={6}
                >
                  <Form.Label className="report-label">
                    Panchayath
                  </Form.Label>

                  <Form.Select
                    value={
                      panId
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);
                      setPanId(
                        e.target.value
                      );
                    }}
                  >
                    <option value="">
                      All Panchayaths
                    </option>

                    {(
                      panchayaths ||
                      []
                    ).map(
                      (
                        item
                      ) => (
                        <option
                          key={
                            item.pan_id
                          }
                          value={
                            item.pan_id
                          }
                        >
                          {item.pan_name}
                        </option>
                      )
                    )}
                  </Form.Select>
                </Col>

                <Col
                  xl={2}
                  md={6}
                >
                  <Form.Label className="report-label">
                    Category
                  </Form.Label>

                  <Form.Select
                    value={
                      category
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);
                      setCategory(
                        e.target.value
                      );
                    }}
                  >
                    <option value="">
                      All Categories
                    </option>
                    <option value="subscription">
                      Subscription
                    </option>
                    <option value="installation">
                      Installation
                    </option>
                    <option value="training">
                      Training
                    </option>
                    <option value="printer">
                      Printer
                    </option>
                    <option value="other">
                      Other
                    </option>
                  </Form.Select>
                </Col>

                <Col
                  xl={2}
                  md={6}
                >
                  <Form.Label className="report-label">
                    Status
                  </Form.Label>

                  <Form.Select
                    value={
                      status
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);
                      setStatus(
                        e.target.value
                      );
                    }}
                  >
                    <option value="">
                      All Status
                    </option>
                    <option value="pending">
                      Pending
                    </option>
                    <option value="invoiced">
                      Invoiced
                    </option>
                    <option value="partially_paid">
                      Partially Paid
                    </option>
                    <option value="cleared">
                      Cleared
                    </option>
                    <option value="cancelled">
                      Cancelled
                    </option>
                  </Form.Select>
                </Col>

                <Col
                  xl={2}
                  md={6}
                >
                  <Form.Label className="report-label">
                    Generated Type
                  </Form.Label>

                  <Form.Select
                    value={
                      generatedType
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);
                      setGeneratedType(
                        e.target.value
                      );
                    }}
                  >
                    <option value="">
                      All Types
                    </option>
                    <option value="automatic">
                      Automatic
                    </option>
                    <option value="manual">
                      Manual
                    </option>
                  </Form.Select>
                </Col>

                <Col
                  xl={2}
                  md={6}
                >
                  <Form.Label className="report-label">
                    From Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);
                      setFromDate(
                        e.target.value
                      );
                    }}
                  />
                </Col>

                <Col
                  xl={2}
                  md={6}
                >
                  <Form.Label className="report-label">
                    To Date
                  </Form.Label>

                  <Form.Control
                    type="date"
                    min={
                      fromDate ||
                      undefined
                    }
                    value={
                      toDate
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);
                      setToDate(
                        e.target.value
                      );
                    }}
                  />
                </Col>

                <Col
                  xl="auto"
                  md={12}
                  className="d-flex align-items-end"
                >
                  <Button
                    variant="outline-secondary"
                    disabled={
                      loading
                    }
                    onClick={
                      clearFilters
                    }
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="report-table-toolbar">
              <div className="report-table-title">
                Billing & Receipt Report{" "}
                <span className="report-count">
                  (
                  {totalRecords.toLocaleString(
                    "en-IN"
                  )}{" "}
                  records)
                </span>
              </div>

              <div className="report-table-right">
                <Button
                  variant="outline-primary"
                  disabled={
                    loading ||
                    exportLoading ||
                    !totalRecords
                  }
                  onClick={() =>
                    handleExport(
                      "excel"
                    )
                  }
                >
                  <Download
                    size={16}
                    className="me-2"
                  />
                  Excel
                </Button>

                <Button
                  variant="outline-danger"
                  disabled={
                    loading ||
                    exportLoading ||
                    !totalRecords
                  }
                  onClick={() =>
                    handleExport(
                      "pdf"
                    )
                  }
                >
                  <Download
                    size={16}
                    className="me-2"
                  />
                  PDF
                </Button>
              </div>
            </div>

            <div className="report-table-wrap">
              <div className="report-table-scroll">
                <Table
                  hover
                  className="mb-0 report-table align-middle"
                >
                  <thead>
                    <tr>
                      <th>
                        Sl. No.
                      </th>

                      <th>
                        {headerButton(
                          "Bill Date",
                          "bill_date"
                        )}
                      </th>

                      <th>
                        {headerButton(
                          "Panchayath",
                          "pan_name"
                        )}
                      </th>

                      <th>
                        Category
                      </th>

                      <th>
                        Billing Period
                      </th>

                      <th>
                        Base Amount
                      </th>

                      <th>
                        GST
                      </th>

                      <th>
                        GST Amount
                      </th>

                      <th>
                        {headerButton(
                          "Final Bill",
                          "bill_amount"
                        )}
                      </th>

                      <th>
                        Invoice No.
                      </th>

                      <th>
                        Invoice Date
                      </th>

                      <th>
                        Invoice Amount
                      </th>

                      <th>
                        Approved Received
                      </th>

                      <th>
                        Pending Receipt
                      </th>

                      <th>
                        Balance
                      </th>

                      <th>
                        Payment Mode
                      </th>

                      <th>
                        Receipt No.
                      </th>

                      <th>
                        Receipt Date
                      </th>

                      <th>
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={
                            19
                          }
                          className="text-center py-4"
                        >
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length ? (
                      rows.map(
                        (
                          row,
                          index
                        ) => (
                          <tr
                            key={
                              row.bill_id ||
                              index
                            }
                          >
                            <td>
                              {safe(
                                row.sl_no
                              )}
                            </td>

                            <td>
                              {safe(
                                row.bill_date
                              )}
                            </td>

                            <td>
                              <strong>
                                {safe(
                                  row.pan_name
                                )}
                              </strong>
                            </td>

                            <td>
                              {labelize(
                                row.bill_category
                              )}
                            </td>

                            <td>
                              {safe(
                                row.billing_period
                              )}
                            </td>

                            <td>
                              {money(
                                row.taxable_amount
                              )}
                            </td>

                            <td>
                              {String(
                                row.tax_type ||
                                  ""
                              ).toUpperCase() ===
                              "GST"
                                ? `${Number(
                                    row.tax_percentage ||
                                      0
                                  )}%`
                                : "Non-GST"}
                            </td>

                            <td>
                              {money(
                                row.tax_amount
                              )}
                            </td>

                            <td>
                              <strong>
                                {money(
                                  row.bill_amount
                                )}
                              </strong>
                            </td>

                            <td>
                              {safe(
                                row.invoice_number
                              )}
                            </td>

                            <td>
                              {safe(
                                row.invoice_date
                              )}
                            </td>

                            <td>
                              {row.invoice_amount ===
                                null ||
                              row.invoice_amount ===
                                undefined
                                ? "—"
                                : money(
                                    row.invoice_amount
                                  )}
                            </td>

                            <td>
                              {money(
                                row.approved_received
                              )}
                            </td>

                            <td>
                              {money(
                                row.pending_received
                              )}
                            </td>

                            <td>
                              <strong>
                                {money(
                                  row.balance_amount
                                )}
                              </strong>
                            </td>

                            <td>
                              {safe(
                                row.payment_modes
                              )}
                            </td>

                            <td>
                              {safe(
                                row.receipt_numbers
                              )}
                            </td>

                            <td>
                              {safe(
                                row.receipt_dates
                              )}
                            </td>

                            <td>
                              {labelize(
                                row.status
                              )}
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            19
                          }
                          className="text-center py-4"
                        >
                          No billing records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="report-pagination-bar">
                <div className="report-page-size">
                  <span>
                    Rows:
                  </span>

                  <Form.Select
                    value={
                      pageSize
                    }
                    className="page-size-select"
                    disabled={
                      loading
                    }
                    onChange={(
                      e
                    ) => {
                      setPage(1);

                      setPageSize(
                        Number(
                          e.target.value
                        )
                      );
                    }}
                  >
                    {[
                      10,
                      20,
                      50,
                      100,
                    ].map(
                      (
                        size
                      ) => (
                        <option
                          key={
                            size
                          }
                          value={
                            size
                          }
                        >
                          {size}
                        </option>
                      )
                    )}
                  </Form.Select>
                </div>

                <ReportPagination
                  loading={
                    loading
                  }
                  page={
                    currentPage
                  }
                  totalPages={
                    totalPages
                  }
                  setPage={
                    setPage
                  }
                />

                <div className="report-page-info">
                  Page{" "}
                  {currentPage} /{" "}
                  {totalPages}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

export default
PanchayathBillingReceiptReportPage;
